import { computed, inject, Injectable, Injector, runInInjectionContext, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  getAllCountriesList,
  getAllDepartments,
  getAllOccupationalGroups,
  getAllProfessions,
  getAllTitles,
  getSelectedExhibitionId
} from '@app/app.reducer';
import {
  DynamicFieldOptionsTranslations,
  DynamicFieldOptionTranslation
} from '@core/data-access/models/dynamic-form-field/dynamic-form-field-translation.model';
import {
  DropdownOption,
  DynamicFormFields,
  FieldKey,
  RadioOption
} from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import { FormName } from '@core/data-access/models/form/form.model';
import { isDropdownField, isRadioField } from '@core/utils/dynamic-form-field/dynamic-form-field-type-guards';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { HelperService } from '@store/helpers/helper.service';
import { getIsTranslationLoaded } from '@store/translation/translation.selectors';
import { map as _map, flatMap, forEach, isEmpty, isNil, keyBy, keys, replace } from 'lodash';
import { combineLatest, filter, first, iif, map, of, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DynamicFieldOptionsTranslationFactory {
  readonly #injector = inject(Injector);
  readonly #store = inject(Store);
  readonly #translateService = inject(TranslateService);
  readonly #helperService = inject(HelperService);

  readonly #state = signal({});

  readonly state = computed<DynamicFieldOptionsTranslations>(() => {
    const state = this.#state();
    const dynamicFieldOptionsTranslations: DynamicFieldOptionsTranslations = {};

    forEach(keys(state), (fieldKey: FieldKey) => (dynamicFieldOptionsTranslations[fieldKey] = state[fieldKey]()));

    return dynamicFieldOptionsTranslations;
  });

  createTranslatedDynamicFieldOptions(
    formName: FormName,
    dynamicFormFields: DynamicFormFields,
    waitForEventTranslationsToLoad: boolean = false
  ) {
    runInInjectionContext(this.#injector, () => {
      const state = this.#state();
      const updateState = {};

      forEach(dynamicFormFields, (dynamicFormField) => {
        if (isDropdownField(dynamicFormField) || isRadioField(dynamicFormField)) {
          const { key, options } = dynamicFormField;

          // FIXME: Radio buttons for newsletters need to be refactored (maybe also questionnaire radio buttons)
          // so that they hold value as true and false and not as translation key that is then mapped to a boolean
          // on save form submission. After that maybe we can refactor how are translations set for field options.
          const fieldKey = key === FieldKey.Newsletter ? `${formName}_${key}` : key;

          if (!isNil(state[fieldKey])) {
            return;
          }

          updateState[fieldKey] =
            key === FieldKey.Country
              ? this.translateCountryFieldOptions()
              : this.translateDynamicFieldOptions(options, waitForEventTranslationsToLoad);
        }
      });

      this.#state.update((currentState) => ({
        ...currentState,
        ...updateState,
        allOptions: this.translateAllDynamicFieldOptions()
      }));
    });
  }

  private translateDynamicFieldOptions(
    fieldOptions: DropdownOption[] | RadioOption[],
    waitForEventTranslationsToLoad: boolean
  ) {
    const waitForEventTranslationsToLoad$ = combineLatest({
      selectedExhibitionId: this.#store.select(getSelectedExhibitionId),
      isTranslationLoaded: this.#store.select(getIsTranslationLoaded)
    }).pipe(
      first(({ selectedExhibitionId, isTranslationLoaded }) => !isNil(selectedExhibitionId) && isTranslationLoaded),
      map(() => fieldOptions)
    );

    return toSignal(
      iif(() => waitForEventTranslationsToLoad, waitForEventTranslationsToLoad$, of(fieldOptions)).pipe(
        filter((fieldOptions) => !isEmpty(fieldOptions)),
        switchMap((fieldOptions) =>
          this.#translateService.stream(_map(fieldOptions, ({ value }) => value)).pipe(
            map((translations): DynamicFieldOptionTranslation[] => {
              if (isEmpty(translations)) {
                return [];
              }

              return _map(fieldOptions, ({ key, value }) => ({
                key,
                value,
                label: translations[value]
              }));
            })
          )
        )
      ),
      {
        initialValue: []
      }
    );
  }

  private translateCountryFieldOptions() {
    return toSignal(
      this.#store.select(getAllCountriesList).pipe(
        switchMap((preferredCountriesOrder) =>
          this.#helperService.loadCountries().pipe(
            switchMap((translateCountries) =>
              this.#translateService.stream(_map(translateCountries, (country) => `country.${country.shortCode}`))
            ),
            map((translatedCountries): DynamicFieldOptionTranslation[] => {
              if (isEmpty(translatedCountries)) {
                return [];
              }

              return _map(keys(translatedCountries), (key) => ({
                key: replace(key, 'country.', ''),
                value: key,
                label: translatedCountries[key]
              })).sort((a, b) => {
                const preferredIndexA: number = preferredCountriesOrder?.indexOf(a.key);
                const preferredIndexB: number = preferredCountriesOrder?.indexOf(b.key);

                if (preferredIndexA > -1 && preferredIndexB > -1) {
                  if (preferredIndexA < preferredIndexB) {
                    return -1;
                  }

                  return 1;
                }

                if (preferredIndexA > -1) {
                  return -1;
                }

                if (preferredIndexB > -1) {
                  return 1;
                }

                return a.label.localeCompare(b.label);
              });
            })
          )
        )
      ),
      {
        initialValue: []
      }
    );
  }

  private translateAllDynamicFieldOptions() {
    return toSignal(
      combineLatest({
        allProfessions: this.#store.select(getAllProfessions),
        allTitles: this.#store.select(getAllTitles),
        allDepartments: this.#store.select(getAllDepartments),
        allOccupationalGroups: this.#store.select(getAllOccupationalGroups)
      }).pipe(
        map((combinedLatest) => keyBy(flatMap(combinedLatest), 'text')),
        filter((fieldOptions) => !isEmpty(fieldOptions)),
        switchMap((fieldOptions) =>
          this.#translateService.stream(keys(fieldOptions)).pipe(
            map((translations): DynamicFieldOptionTranslation[] => {
              if (isEmpty(translations)) {
                return [];
              }

              return _map(fieldOptions, ({ id, text }) => ({
                key: id,
                value: text,
                label: translations[text]
              }));
            })
          )
        )
      )
    );
  }
}
